import type { HTMLAttributes } from 'react';

import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { TestId } from 'src/testids';
import type { AccountLinkingMismatchError } from 'src/utils/errors';
import { userHelpers } from 'src/utils/userHelpers';

import type { User } from '@auth0/auth0-react';
import type { RmClaims } from 'src/api/queries/user';
import { Link2 } from './Link2';
import { NotificationBox } from './NotificationBox';

interface Props extends HTMLAttributes<HTMLDivElement> {
  error: AccountLinkingMismatchError;
}

export function ErrorAccountLinkingMismatch({ error, ...props }: Props) {
  let currentUserEmail =
    userHelpers.getEmail(error.currentUser) ||
    userHelpers.getEmailFromClaims(error.currentUser);
  let expectedUserEmail =
    userHelpers.getEmail(error.expectedUser) ||
    userHelpers.getEmailFromClaims(error.expectedUser);

  const getAccountProviderInfo = (user: User | RmClaims) => {
    const provider = userHelpers.getUserAccountProvider(user);

    if (provider === 'Auth0') {
      return 'username/password';
    }

    if (provider === 'SAML') {
      return 'single sign-on';
    }

    return provider;
  };

  if (currentUserEmail === expectedUserEmail) {
    currentUserEmail += ` (${getAccountProviderInfo(error.currentUser)})`;
    expectedUserEmail += ` (${getAccountProviderInfo(error.expectedUser)})`;
  }

  return (
    <div
      data-cy={TestId.PageEnterpriseEnrollmentAccountLinkingMismatchError}
      {...props}
    >
      <div className="m-auto flex w-full max-w-screen-md flex-col items-center gap-24 px-24 py-64 text-center md:px-64 lg:py-128">
        <Typography variant="heading-lg" className="text-center">
          SSO verification failed
        </Typography>
        <Typography variant="body-md">
          We expected you to log in with{' '}
          <b>{expectedUserEmail || 'unknown email'}</b>, but you were logged in
          as <b>{currentUserEmail || 'unknown email'}</b> instead.
        </Typography>

        <NotificationBox
          variant="info"
          title="Troubleshooting"
          className="mt-32"
        >
          <ul className="flex list-disc flex-col gap-8 pt-8 pl-24">
            <li>
              <Typography variant="body-md">
                <em>
                  If you did not see a log in screen after logging in to SSO
                </em>
                , you might already be in an active SSO session on a different
                account. Sign out of that session first or switch to a
                private/incognito window and try again.
              </Typography>
            </li>
            <li>
              <Typography variant="body-md">
                <em>If you did sign in with the correct email</em>, it might be
                that your identity provider reports a different email address
                for your account. Please verify that the attribute mapping in
                your identity provider reports the correct email address on the{' '}
                <code>email</code> attribute and that the attribute name is an
                exact match.
              </Typography>
            </li>
          </ul>
        </NotificationBox>

        <Link2
          to="/login"
          search={{
            redirect: error.redirect,
            login_hint: expectedUserEmail,
            connection:
              userHelpers.getConnectionFromUserId(error.expectedUser) ??
              undefined,
          }}
          componentLocation={
            ComponentLocations.ENTERPRISE.ACCOUNT_LINKING_MISMATCH
          }
          as="button"
          variant="primary"
          className="ark-button mt-32 ark-button--primary"
        >
          Sign back in
        </Link2>
      </div>
    </div>
  );
}
